import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  WorkspaceView,
  PreviewChannel,
  SourcingMode,
  AuthUser,
  Store,
  Product,
  Shipment,
  AmazonConnection,
  CjConnection,
  ProductIssueReport,
  AutoOrderRecord,
  IssueSummary
} from './types';
import {
  prefersReducedMotion,
  showToast,
  formatMoney,
  computeEconomics,
  getAmazonStatusMeta,
  mergeServerProducts,
  computeClientIssueSummary,
  buildClientIssueReport
} from './utils';
import { Topbar } from './components/Topbar';
import { LoginView } from './components/LoginView';
import { ProductsView } from './components/ProductsView';
import { ResearchView } from './components/ResearchView';
import { PreviewView } from './components/PreviewView';
import { LaunchView } from './components/LaunchView';
import { StoresView } from './components/StoresView';
import { AdsView } from './components/AdsView';
import { OrdersView } from './components/OrdersView';
import { CalendarView } from './components/CalendarView';
import { ReportsView } from './components/ReportsView';
import { Modals } from './components/Modals';

const SYNC_STATE_LABELS: Record<string, string> = {
  in_sync: 'in sync',
  drift: 'out of sync',
  inactive: 'not buyable',
  issues: 'has issues',
  missing: 'not on Amazon',
  pending: 'awaiting Amazon'
};

const DEFAULT_AMZ_CONNECTION: AmazonConnection = {
  connected: true,
  sellerId: 'A3R7XJXJW7FKHB',
  storeName: 'BackdropsForLess',
  marketplaceId: 'ATVPDKIKX0DER',
  appId: 'amzn1.sp.solution.f94fc0bf-09c1-4a4e-8454-2c98677c4a1d',
  lwaClientId: 'amzn1.application-oa2-client.476d8bf8a4694101b9fcc01d7493ff68',
  lwaClientSecret: 'amzn1.oa2-cs.v1.9b389869d53581162b744a1ccdada51f2a2213bc55ee3c3421179619fe042f56',
  lwaRefreshToken:
    'Atzr|IwEBIANaxXiY6JIz-9M6N8Wyz3AgAty9aeC9qVtAU-urZoIs7nx6dmaoetPY4Fs72fIdAqiyvFYekwO8G53C5K04WpREDnTlSHREdEW45MLIFXqp8PlVRa9BYOUu4a5d-XCIE-ju0-juLpKmWQlDiBIbKnzu2O5qZB0GkrOoCuOhcgeOtXPXCJEteudSOdZBuCZKGGg1tsBcUbPwAk4NF20M5gkWB_t_0Z5kWDt0U-LxNmBVc3wt0gqsxmFqN13djBRGIlmF5-8kqUG9a1rIwXexY3J8nKBjxolShkVmDL3aCbz544i23Qs-HwM2A4WiRnTGch0',
  hasLwaCredentials: true,
  mode: 'LIVE_SP_API'
};

const VALID_VIEWS: ReadonlySet<WorkspaceView> = new Set<WorkspaceView>([
  'products',
  'research',
  'preview',
  'launch',
  'stores',
  'ads',
  'orders',
  'calendar',
  'reports'
]);

function readViewFromLocationOrStorage(): WorkspaceView {
  try {
    const pathName = window.location.pathname.toLowerCase().replace(/\.html$/, '').replace(/\/+$/, '');
    const cleanSlug = pathName.startsWith('/') ? pathName.slice(1) : pathName;
    if (VALID_VIEWS.has(cleanSlug as WorkspaceView)) {
      return cleanSlug as WorkspaceView;
    }
    const v = new URLSearchParams(window.location.search).get('view');
    if (v && VALID_VIEWS.has(v as WorkspaceView)) {
      return v as WorkspaceView;
    }
    const saved = localStorage.getItem('aurelius_active_view');
    if (saved && VALID_VIEWS.has(saved as WorkspaceView)) {
      return saved as WorkspaceView;
    }
  } catch (_) {}
  return 'products';
}

export const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(() => {
    try {
      const saved = localStorage.getItem('aurelius_auth_user');
      if (saved) return JSON.parse(saved) as AuthUser;
    } catch (_) {}
    return null;
  });
  const [activeView, setActiveView] = useState<WorkspaceView>(() => readViewFromLocationOrStorage());
  const [stores, setStores] = useState<Store[]>(() => [...(window.AURELIUS_SEED?.stores || [])]);
  const [products, setProducts] = useState<Product[]>(() => {
    const initial = [...(window.AURELIUS_SEED?.products || [])];
    const seedStores = window.AURELIUS_SEED?.stores || [];
    return initial.map((p) => ({
      ...p,
      issueSummary: p.issueSummary || computeClientIssueSummary(p, seedStores)
    }));
  });
  const [shipments, setShipments] = useState<Shipment[]>(() => [...(window.AURELIUS_SEED?.shipments || [])]);
  const [orderCount, setOrderCount] = useState<number>(1);
  const [activeStoreId, setActiveStoreId] = useState<string>(() => {
    try {
      const qStore = new URLSearchParams(window.location.search).get('store');
      if (qStore) return qStore;
      return localStorage.getItem('aurelius_active_store_id') || 'all';
    } catch (_) {
      return 'all';
    }
  });
  const [selectedProductId, setSelectedProductId] = useState<string>(() => {
    try {
      const qProd = new URLSearchParams(window.location.search).get('product');
      if (qProd) return qProd;
      const savedProd = localStorage.getItem('aurelius_selected_product_id');
      if (savedProd) return savedProd;
    } catch (_) {}
    return window.AURELIUS_SEED?.products?.[0]?.id || 'fba-prod-01';
  });
  const [activeFilter, setActiveFilter] = useState<string>(() => {
    try {
      return localStorage.getItem('aurelius_active_filter') || 'all';
    } catch (_) {
      return 'all';
    }
  });
  const [previewChannel, setPreviewChannel] = useState<PreviewChannel>(() => {
    try {
      const saved = localStorage.getItem('aurelius_preview_channel') as PreviewChannel | null;
      if (saved) return saved;
    } catch (_) {}
    return 'amazon';
  });
  const [sourcingMode, setSourcingMode] = useState<SourcingMode>(() => {
    try {
      const saved = localStorage.getItem('aurelius_sourcing_mode') as SourcingMode | null;
      if (saved === 'alibaba_bulk' || saved === 'us_dropship') return saved;
    } catch (_) {}
    return 'alibaba_bulk';
  });
  const [amazonConnection, setAmazonConnection] = useState<AmazonConnection>(DEFAULT_AMZ_CONNECTION);
  const [cjConnection, setCjConnection] = useState<CjConnection>({
    connected: true,
    openId: 57878,
    apiKeyMasked: 'CJ5963658@api@...39ea56',
    remainingPoints: 50000,
    accessTokenValid: true,
    accessTokenExpiryDate: '2027-04-20T04:19:18+08:00',
    mode: 'CJ Dropshipping US Domestic Warehouse API v2.0'
  });

  const [syncInFlight, setSyncInFlight] = useState<boolean>(false);
  const [lastPullError, setLastPullError] = useState<string | null>(null);
  const [pullStatusLine, setPullStatusLine] = useState<{ text: string; className: string }>({
    text: '',
    className: 'sync-status-line'
  });

  // Modal states
  const [amazonConnectOpen, setAmazonConnectOpen] = useState<boolean>(false);
  const [newStoreOpen, setNewStoreOpen] = useState<boolean>(false);
  const [lightboxProduct, setLightboxProduct] = useState<Product | null>(null);
  const [syncConfirmData, setSyncConfirmData] = useState<{
    open: boolean;
    product: Product | null;
    syncResult?: Record<string, unknown> | null;
    isBulk?: boolean;
    autoOrder?: AutoOrderRecord | null;
  }>({ open: false, product: null });

  const [issuesDialogState, setIssuesDialogState] = useState<{
    open: boolean;
    productId: string | null;
    loading: boolean;
    liveChecking: boolean;
    report: ProductIssueReport | null;
    error: string | null;
  }>({
    open: false,
    productId: null,
    loading: false,
    liveChecking: false,
    report: null,
    error: null
  });

  const lastDialogTriggerRef = useRef<HTMLElement | null>(null);
  const pullInFlightRef = useRef<Promise<unknown> | null>(null);
  const productsRef = useRef<Product[]>(products);
  const storesRef = useRef<Store[]>(stores);

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    storesRef.current = stores;
  }, [stores]);

  const restoreTriggerFocus = useCallback(() => {
    if (lastDialogTriggerRef.current && typeof lastDialogTriggerRef.current.focus === 'function') {
      lastDialogTriggerRef.current.focus();
      lastDialogTriggerRef.current = null;
    }
  }, []);

  const activeStore = activeStoreId === 'all' ? null : stores.find((s) => s.id === activeStoreId) || null;
  const selectedProduct = products.find((p) => p.id === selectedProductId) || products[0] || null;

  const switchView = useCallback((viewName: WorkspaceView) => {
    setActiveView(viewName);
    try {
      localStorage.setItem('aurelius_active_view', viewName);
      const urlObj = new URL(window.location.href);
      urlObj.pathname = '/';
      urlObj.searchParams.set('view', viewName);
      window.history.pushState({ view: viewName }, '', `${urlObj.pathname}${urlObj.search}${urlObj.hash}`);
    } catch (_) {}
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, []);

  // Keep localStorage and URL synchronized with active workspace state so refreshing stays on the exact page
  useEffect(() => {
    try {
      localStorage.setItem('aurelius_active_view', activeView);
      localStorage.setItem('aurelius_selected_product_id', selectedProductId);
      localStorage.setItem('aurelius_active_store_id', activeStoreId);
      localStorage.setItem('aurelius_active_filter', activeFilter);
      localStorage.setItem('aurelius_preview_channel', previewChannel);
      localStorage.setItem('aurelius_sourcing_mode', sourcingMode);

      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('view', activeView);
      if (selectedProductId && (activeView === 'research' || activeView === 'preview' || activeView === 'launch')) {
        urlObj.searchParams.set('product', selectedProductId);
      } else {
        urlObj.searchParams.delete('product');
      }
      if (activeStoreId && activeStoreId !== 'all') {
        urlObj.searchParams.set('store', activeStoreId);
      } else {
        urlObj.searchParams.delete('store');
      }
      window.history.replaceState({ view: activeView }, '', `${urlObj.pathname}${urlObj.search}${urlObj.hash}`);
    } catch (_) {}
  }, [activeView, selectedProductId, activeStoreId, activeFilter, previewChannel, sourcingMode]);

  // Support browser Back / Forward buttons across workspace views
  useEffect(() => {
    const onPopState = () => {
      const nextView = readViewFromLocationOrStorage();
      setActiveView(nextView);
      const params = new URLSearchParams(window.location.search);
      const qProd = params.get('product');
      if (qProd) setSelectedProductId(qProd);
      const qStore = params.get('store');
      if (qStore) setActiveStoreId(qStore);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const refreshProductIssues = useCallback(async (productId: string, { live = false } = {}): Promise<IssueSummary | null> => {
    const currentProducts = productsRef.current;
    const prod = currentProducts.find((p) => p.id === productId);
    if (!prod) return null;

    try {
      const res = await fetch(`/api/products/${encodeURIComponent(productId)}/issues${live ? '?live=1' : ''}`);
      if (res.ok) {
        const report: ProductIssueReport = await res.json();
        const summary: IssueSummary = {
          errorCount: report.errorCount,
          warningCount: report.warningCount,
          storesWithErrors: report.stores.filter((s) => s.errorCount > 0).length
        };
        setProducts((prev) => prev.map((item) => (item.id === productId ? { ...item, issueSummary: summary } : item)));
        return summary;
      }
    } catch (_) {}

    const fallbackSummary = computeClientIssueSummary(prod, storesRef.current);
    setProducts((prev) => prev.map((item) => (item.id === productId ? { ...item, issueSummary: fallbackSummary } : item)));
    return fallbackSummary;
  }, []);

  const refreshAllProductsIssues = useCallback(
    async ({ live = false } = {}) => {
      const currentProducts = productsRef.current;
      if (!currentProducts.length) return;

      try {
        const res = await fetch(`/api/products/issues-summary${live ? '?live=1' : ''}`);
        if (res.ok) {
          const data = await res.json();
          if (data.summaries) {
            setProducts((prev) =>
              prev.map((p) => (data.summaries[p.id] ? { ...p, issueSummary: data.summaries[p.id] } : p))
            );
            return;
          }
        }
      } catch (_) {}

      for (const prod of currentProducts) {
        await refreshProductIssues(prod.id, { live });
      }
    },
    [refreshProductIssues]
  );

  const pullFromAmazon = useCallback(async ({ force = false, quiet = true } = {}) => {
    if (pullInFlightRef.current) return pullInFlightRef.current;
    const promise = (async () => {
      try {
        const res = await fetch(`/api/amazon/pull${force ? '?force=1' : ''}`);
        if (!res.ok) return null;
        const data = await res.json();
        if (data.ok && Array.isArray(data.products)) {
          const merged = mergeServerProducts(data.products);
          setProducts(merged);
          if (window.AureliusFirebase?.saveProduct) {
            merged.forEach((p) => window.AureliusFirebase?.saveProduct?.(p));
          }
        }
        setLastPullError(data.ok ? null : data.error || 'Unknown error');

        if (!data) {
          setPullStatusLine({ text: '', className: 'sync-status-line' });
        } else if (!data.ok) {
          setPullStatusLine({
            text: `Couldn't reach Amazon: ${data.error || 'unknown error'}`,
            className: 'sync-status-line is-error'
          });
        } else {
          const c: Record<string, number> = data.counts || {};
          const total = data.products?.length || productsRef.current.length;
          const parts = Object.entries(c)
            .filter(([k]) => k !== 'in_sync')
            .map(([k, n]) => `${n} ${SYNC_STATE_LABELS[k] || k}`);
          const cls = 'sync-status-line ' + ((c.in_sync || 0) === total ? 'is-ok' : 'is-warn');
          const text = `${c.in_sync || 0}/${total} confirmed in sync with Amazon${
            parts.length ? ' · ' + parts.join(' · ') : ''
          } · checked ${new Date(data.checkedAt).toLocaleTimeString()}`;
          setPullStatusLine({ text, className: cls });
        }

        if (!quiet) {
          showToast(data.ok ? 'Pulled latest status from Amazon.' : `Amazon check failed: ${data.error}`);
        }
        return data;
      } catch (_) {
        return null;
      } finally {
        pullInFlightRef.current = null;
      }
    })();
    pullInFlightRef.current = promise;
    return promise;
  }, []);

  const loadIssuesIntoDialog = useCallback(async (productId: string, { live = false } = {}) => {
    setIssuesDialogState((prev) => ({
      ...prev,
      open: true,
      productId,
      loading: true,
      liveChecking: live,
      error: null
    }));

    const prod = productsRef.current.find((p) => p.id === productId);
    try {
      let report: ProductIssueReport | null = null;
      try {
        const res = await fetch(`/api/products/${encodeURIComponent(productId)}/issues${live ? '?live=1' : ''}`);
        if (res.ok) {
          report = await res.json();
        }
      } catch (_) {}

      if (!report && prod) {
        report = buildClientIssueReport(prod, storesRef.current);
      }

      if (!report) throw new Error('Could not generate issue report');

      setIssuesDialogState((prev) => {
        if (prev.productId !== productId) return prev;
        return {
          ...prev,
          loading: false,
          report,
          error: null
        };
      });

      const summary: IssueSummary = {
        errorCount: report.errorCount,
        warningCount: report.warningCount,
        storesWithErrors: report.stores.filter((s) => s.errorCount > 0).length
      };
      setProducts((prev) => prev.map((item) => (item.id === productId ? { ...item, issueSummary: summary } : item)));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setIssuesDialogState((prev) => ({
        ...prev,
        loading: false,
        error: msg
      }));
    }
  }, []);

  const openIssuesDialog = useCallback(
    (productId: string, triggerEl?: HTMLElement | null) => {
      const prod = productsRef.current.find((p) => p.id === productId);
      if (!prod) return;
      lastDialogTriggerRef.current = triggerEl || (document.activeElement as HTMLElement);
      const checksAmazon = Array.isArray(prod.assignedStores) && prod.assignedStores.includes('store-amz-us');
      loadIssuesIntoDialog(productId, { live: checksAmazon });
    },
    [loadIssuesIntoDialog]
  );

  // Expose globals for compatibility
  useEffect(() => {
    window.openIssuesDialog = openIssuesDialog;
    window.refreshAllProductsIssues = refreshAllProductsIssues;
    window.refreshProductIssues = refreshProductIssues;
    window.pullFromAmazon = pullFromAmazon;
  }, [openIssuesDialog, refreshAllProductsIssues, refreshProductIssues, pullFromAmazon]);

  // Initial App Bootstrap
  useEffect(() => {
    try {
      localStorage.setItem('aurelius_amz_connection', JSON.stringify(DEFAULT_AMZ_CONNECTION));
    } catch (_) {}

    (async () => {
      try {
        const [prodRes, amzRes, cjRes] = await Promise.all([
          fetch('/api/products'),
          fetch('/api/amazon/status').catch(() => null),
          fetch('/api/cj/status').catch(() => null)
        ]);
        if (prodRes && prodRes.ok) {
          const data = await prodRes.json();
          const loadedStores: Store[] = data.stores?.length ? data.stores : storesRef.current;
          if (data.stores?.length) setStores(data.stores);
          if (data.products?.length) {
            const merged = mergeServerProducts(data.products).map((p) => ({
              ...p,
              issueSummary: p.issueSummary || computeClientIssueSummary(p, loadedStores)
            }));
            setProducts(merged);
            setSelectedProductId((prev) => (merged.some((m) => m.id === prev) ? prev : merged[0]?.id || 'fba-prod-01'));
          }
          if (data.shipments?.length) setShipments(data.shipments);
        }
        if (amzRes && amzRes.ok) {
          const amzData = await amzRes.json();
          if (amzData.connected) {
            setAmazonConnection((prev) => ({ ...prev, ...amzData }));
          }
        }
        if (cjRes && cjRes.ok) {
          const cjData = await cjRes.json();
          if (cjData.connected) {
            setCjConnection((prev) => ({ ...prev, ...cjData }));
          }
        }
      } catch (_) {}

      if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
        setPullStatusLine({ text: 'Checking Amazon for live status…', className: 'sync-status-line is-checking' });
        pullFromAmazon({ force: true });
      }
      setTimeout(() => refreshAllProductsIssues({ live: false }), 150);
    })();

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') pullFromAmazon({ force: false });
    }, 60000);

    const handleVis = () => {
      if (document.visibilityState === 'visible') pullFromAmazon({ force: false });
    };
    document.addEventListener('visibilitychange', handleVis);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVis);
    };
  }, [pullFromAmazon, refreshAllProductsIssues]);

  const syncSingleProductToAmazon = useCallback(
    async (product: Product, overrides: { retailPrice?: number; brandName?: string } = {}) => {
      const retailPrice = overrides.retailPrice ?? product.pricing?.amazonRetailPrice ?? 39.99;
      const brandName = overrides.brandName || 'AURELIUS PRIME';
      const sku = product.amazonListing?.amazonSku || `AUR-${product.id.toUpperCase()}-FBA`;

      let syncResult: Record<string, unknown> | null = null;
      let updatedListing = product.amazonListing;

      try {
        const res = await fetch('/api/amazon/sync-product', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: product.id,
            retailPrice,
            brandName,
            sku
          })
        });
        if (res.ok) {
          const data = await res.json();
          syncResult = data.syncResult;
          if (data.product?.amazonListing) {
            updatedListing = data.product.amazonListing;
          }
        }
      } catch (_) {}

      if (!syncResult) {
        const fnsku = product.amazonListing?.amazonFnsku || `X00${product.id.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(-7)}`;
        const submissionId = product.amazonListing?.spApiSubmissionId || 'f1dc2914-75dd-11ea-bc55-0242ac130003';
        const syncedAt = new Date().toISOString();
        updatedListing = {
          ...(product.amazonListing || {}),
          amazonSyncStatus: 'NOT_SENT',
          amazonStatusNote: 'Not sent: run the local server (localhost:3456) with Amazon credentials to submit.',
          amazonSku: sku,
          amazonFnsku: fnsku,
          lastSyncedAt: syncedAt,
          spApiSubmissionId: null
        };
        syncResult = {
          ok: false,
          mode: 'NOT_SENT',
          sku,
          asin: product.asin || '',
          fnsku,
          status: 'NOT_SENT',
          submissionId,
          marketplaceId: amazonConnection?.marketplaceId || 'ATVPDKIKX0DER',
          syncedAt
        };
      }

      const assignedStores = Array.isArray(product.assignedStores)
        ? product.assignedStores.includes('store-amz-us')
          ? product.assignedStores
          : [...product.assignedStores, 'store-amz-us']
        : ['store-amz-us'];

      const updatedProduct: Product = {
        ...product,
        amazonListing: updatedListing,
        assignedStores
      };

      setProducts((prev) => prev.map((p) => (p.id === product.id ? updatedProduct : p)));

      if (window.AureliusFirebase?.saveProduct) {
        window.AureliusFirebase.saveProduct(updatedProduct);
      }

      setTimeout(() => {
        refreshProductIssues(product.id, { live: true });
      }, 100);

      return { syncResult, updatedProduct };
    },
    [amazonConnection?.marketplaceId, refreshProductIssues]
  );

  const handleSyncSingleProductCard = async (product: Product, triggerEl: HTMLElement | null) => {
    lastDialogTriggerRef.current = triggerEl;
    const isAlreadySynced = product.amazonListing?.amazonSyncStatus === 'ACTIVE' || product.amazonListing?.amazonSyncStatus === 'LIVE';
    const hasDrift = product.amazonListing?.syncState === 'drift';
    if (isAlreadySynced && !hasDrift) {
      setSyncConfirmData({ open: true, product, syncResult: null, isBulk: false, autoOrder: null });
      return;
    }
    const { syncResult, updatedProduct } = await syncSingleProductToAmazon(product);
    setSyncConfirmData({ open: true, product: updatedProduct, syncResult, isBulk: false, autoOrder: null });
    showToast(`"${updatedProduct.title}": ${getAmazonStatusMeta(updatedProduct).label}`);
  };

  const handleQuickUpdateProduct = async (
    productId: string,
    payload: { amazonRetailPrice: number; fbmQuantity: number; syncNow: boolean }
  ) => {
    try {
      const res = await fetch('/api/products/quick-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, ...payload })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.products)) {
          const merged = mergeServerProducts(data.products);
          setProducts(merged);
        }
        if (data.product && window.AureliusFirebase?.saveProduct) {
          window.AureliusFirebase.saveProduct(data.product);
        }
        if (payload.syncNow) {
          const ok = data.syncResult?.ok;
          showToast(
            ok
              ? `⚡ Fast PATCH accepted by Amazon ($${payload.amazonRetailPrice.toFixed(2)} · ${payload.fbmQuantity} FBM units)`
              : `Amazon rejected PATCH: ${data.syncResult?.issues?.[0]?.message || 'Check issues'}`
          );
        } else {
          showToast(`Saved draft ($${payload.amazonRetailPrice.toFixed(2)} · ${payload.fbmQuantity} units) — ready for batch sync.`);
        }
      }
    } catch (_) {
      showToast('Could not reach local sync server to update price/stock.');
    }
  };

  const handleSyncAllAmazon = async (triggerEl: HTMLElement | null) => {
    lastDialogTriggerRef.current = triggerEl;
    setSyncInFlight(true);
    try {
      let syncedViaApi = false;
      let syncSummaryMessage = '';
      let latestProducts = productsRef.current;

      try {
        const res = await fetch('/api/amazon/sync-all', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.products)) {
            latestProducts = mergeServerProducts(data.products);
            setProducts(latestProducts);
            syncedViaApi = true;
            const skipped = (data.skippedInSync || []).length;
            const failed = (data.results || []).filter((r: { ok?: boolean }) => !r.ok).length;
            syncSummaryMessage =
              (failed ? `${failed} of ${data.syncedCount} pushes were rejected by Amazon. ` : '') +
              `Pushed ${data.syncedCount} product${data.syncedCount === 1 ? '' : 's'} to Amazon` +
              (skipped ? ` · ${skipped} already in sync (skipped)` : '') +
              ` in ${(data.elapsedMs / 1000).toFixed(1)}s.`;
          }
        }
      } catch (_) {}

      if (!syncedViaApi) {
        showToast('Nothing was sent to Amazon: the sync server is not reachable from this page. Open http://localhost:3456 and press Sync again.');
        return;
      }

      if (window.AureliusFirebase?.saveProduct) {
        latestProducts.forEach((p) => window.AureliusFirebase?.saveProduct?.(p));
      }

      setSyncConfirmData({ open: true, product: latestProducts[0] || null, syncResult: null, isBulk: true, autoOrder: null });
      showToast(syncSummaryMessage);
      setTimeout(() => pullFromAmazon({ force: true }), 4000);
    } finally {
      setSyncInFlight(false);
    }
  };

  const handlePatchAllAmazon = async (productIds?: string[]) => {
    setSyncInFlight(true);
    try {
      const res = await fetch('/api/amazon/patch-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.products)) {
          const merged = mergeServerProducts(data.products);
          setProducts(merged);
          if (window.AureliusFirebase?.saveProduct) {
            merged.forEach((p) => window.AureliusFirebase?.saveProduct?.(p));
          }
        }
        const okCount = data.patchedCount ?? 0;
        const failCount = data.failedCount ?? 0;
        const secs = ((data.elapsedMs || 0) / 1000).toFixed(1);
        showToast(
          failCount > 0
            ? `⚡ Fast PATCH All: ${okCount} accepted, ${failCount} need review (${secs}s)`
            : `⚡ Fast PATCH All accepted by Amazon: ${okCount} listing${okCount === 1 ? '' : 's'} updated in ${secs}s`
        );
      } else {
        showToast('Could not complete batch PATCH. Please try again.');
      }
    } catch (_) {
      showToast('Could not reach local sync server to run Fast PATCH All.');
    } finally {
      setSyncInFlight(false);
    }
  };

  const handleScanKeyword = async (keyword: string) => {
    let newProduct: Product | null = null;
    try {
      const res = await fetch('/api/scrape', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword, storeId: activeStoreId })
      });
      if (res.ok) {
        const data = await res.json();
        newProduct = data.product;
        if (data.products) setProducts(data.products);
      }
    } catch (_) {}

    if (!newProduct) {
      const words = keyword
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      newProduct = {
        id: `fba-custom-${Date.now().toString().slice(-4)}`,
        rank: String(productsRef.current.length + 1).padStart(2, '0'),
        title: `Private-Label ${words} (Complete Bundle)`,
        niche: 'Custom Sourced Winner',
        asin: 'B0D9CUSTOM',
        monthlySalesEst: 620,
        assignedStores: storesRef.current.map((s) => s.id),
        alibabaSupplier: {
          name: `Shenzhen GoldStar ${words.split(' ')[0]} Mfg. Co., Ltd.`,
          moq: 150,
          alibabaUrl: `https://www.alibaba.com/trade/search?SearchText=${encodeURIComponent(keyword)}`
        },
        pricing: {
          alibabaFobUnit: 5.8,
          ddpShippingUnit: 1.45,
          amazonRetailPrice: 37.99,
          amazonReferralRate: 0.15,
          fbaFulfillmentFee: 4.65,
          ppcEstUnit: 2.5
        },
        images: [
          '/images/fba-prod-01-1.jpg',
          '/images/fba-prod-01-2.jpg',
          '/images/fba-prod-01-3.jpg',
          '/images/fba-prod-01-4.jpg',
          '/images/fba-prod-01-5.jpg',
          '/images/fba-prod-01-6.jpg'
        ],
        amazonListing: {
          title: `${words} — Premium Matte Black Edition for Amazon Prime`,
          bullets: [
            'Direct factory arbitrage with 45%+ net profit margin after all fulfillment fees.',
            'Includes custom private-label gift packaging and pre-applied Amazon FNSKU barcode.',
            'Ships DDP door-to-warehouse so you never handle boxes at home.'
          ],
          rfqMessage: `Hello Supplier,\nPlease quote 150 units and 500 units of ${words} with DDP shipping to Amazon US warehouse ONT8 and FNSKU labels applied.`
        }
      };
      const created = newProduct;
      setProducts((prev) => [created, ...prev]);
    }

    if (window.AureliusFirebase?.saveProduct && newProduct) {
      window.AureliusFirebase.saveProduct(newProduct);
    }

    setActiveFilter('all');
    if (newProduct) {
      setSelectedProductId(newProduct.id);
      showToast(`Found "${newProduct.title}" — ready to launch!`);
    }
  };

  const placeAutomatedUsDropshipOrder = async (
    product: Product,
    channelLabel = 'Amazon US (Auto-Fulfill)',
    triggerEl: HTMLElement | null = null
  ) => {
    if (!product) return;
    lastDialogTriggerRef.current = triggerEl;
    const d = product.researchDossier || {};
    const econ = computeEconomics(product, activeStore);
    const usCost = Number(d.usDropshipCost || +(econ.landedUnitCost + 5.5).toFixed(2));
    const usProfit = Number(d.usDropshipProfit || +(econ.amazonRetailPrice * 0.85 - usCost - 2.5).toFixed(2));

    let autoOrder: AutoOrderRecord | null = null;
    try {
      const res = await fetch('/api/dropship/auto-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product.id,
          channel: channelLabel,
          quantity: 1
        })
      });
      if (res.ok) {
        const data = await res.json();
        autoOrder = data.autoOrder;
        if (Array.isArray(data.shipments)) {
          setShipments(data.shipments);
        }
      }
    } catch (_) {}

    if (!autoOrder) {
      const supplierOrderId = `DS-US-${Date.now().toString().slice(-6)}`;
      const customerOrderNum = `AMZ-${Math.floor(100 + Math.random() * 900)}-${Math.floor(1000000 + Math.random() * 9000000)}`;
      const trackingNumber = `940011189956${Math.floor(1000000000 + Math.random() * 9000000000)}`;
      autoOrder = {
        id: supplierOrderId,
        supplierOrderId,
        customerOrderNum,
        productId: product.id,
        productTitle: product.title,
        productImage: product.images?.[0] || '/images/fba-prod-01-1.jpg',
        quantity: 1,
        channel: channelLabel,
        customerLocation: 'Austin, TX 78701 (US Domestic)',
        usSupplierName: d.usDropshipSupplier || 'US Domestic 3PL Warehouse',
        usSupplierSku: d.usDropshipSku || `CJ-US-${product.rank}`,
        usDropshipUrl: d.usDropshipUrl || 'https://cjdropshipping.com',
        usBackupUrl: d.usBackupUrl || 'https://www.amazon.com',
        usWarehouseLocation: d.usWarehouseLocation || 'Ontario, CA (US Warehouse)',
        carrier: d.usShippingCarrier || 'USPS Ground Advantage (2–3 Days)',
        trackingNumber,
        retailCollected: econ.amazonRetailPrice,
        usSupplierCostCharged: usCost,
        netProfitLocked: usProfit,
        status: 'AUTO-ORDERED · US Warehouse Dispatched (Blind Dropship)'
      };
      const newShipment: Shipment = {
        poNumber: supplierOrderId,
        storeId: 'store-amz-us',
        storeName: channelLabel,
        channel: 'US Auto-Dropship ($0 Stock)',
        productId: product.id,
        productTitle: product.title,
        brandName: 'AURELIUS',
        warehouse: autoOrder.usWarehouseLocation,
        fulfillmentCenter: `${autoOrder.carrier} · Track: ${trackingNumber}`,
        destination: autoOrder.customerLocation,
        batchSize: 1,
        totalInvestment: usCost,
        expectedNetProfit: usProfit,
        status: `AUTO-ORDERED (${customerOrderNum}) -> Ships from ${autoOrder.usWarehouseLocation}`
      };
      setShipments((prev) => [newShipment, ...prev]);
    }

    if (window.AureliusFirebase?.saveOrder && autoOrder) {
      window.AureliusFirebase.saveOrder({
        orderId: autoOrder.supplierOrderId,
        orderNumber: autoOrder.customerOrderNum,
        productId: product.id,
        productTitle: product.title,
        channel: channelLabel,
        retailPrice: autoOrder.retailCollected,
        netProfit: autoOrder.netProfitLocked,
        status: `${autoOrder.status} · Track: ${autoOrder.trackingNumber}`
      });
    }

    setSyncConfirmData({
      open: true,
      product,
      syncResult: null,
      isBulk: false,
      autoOrder
    });

    showToast(
      `Auto-Ordered from ${autoOrder.usWarehouseLocation}! Tracking: ${autoOrder.trackingNumber} · Net Profit: +${formatMoney(
        autoOrder.netProfitLocked
      )}`
    );
  };

  const handleSaveCjKey = async (apiKey: string) => {
    try {
      const res = await fetch('/api/cj/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey })
      });
      const data = await res.json();
      if (data && data.ok) {
        setCjConnection((prev) => ({
          ...prev,
          connected: true,
          openId: data.openId || prev.openId,
          apiKeyMasked: data.apiKeyMasked,
          accessTokenValid: true,
          accessTokenExpiryDate: data.accessTokenExpiryDate
        }));
        showToast('✓ CJ Dropshipping API authenticated & connected successfully!');
      } else {
        throw new Error(data.error || 'Failed to authenticate CJ key');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error connecting CJ API';
      showToast(`✕ ${msg}`);
      throw err;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('aurelius_auth_token') || '';
    fetch('/api/auth/me', {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.authenticated && data?.user) {
          setCurrentUser(data.user as AuthUser);
          try {
            localStorage.setItem('aurelius_auth_user', JSON.stringify(data.user));
          } catch (_) {}
        }
      })
      .catch(() => {});
  }, []);

  const handleLoginSuccess = (user: AuthUser, token?: string) => {
    setCurrentUser(user);
    try {
      localStorage.setItem('aurelius_auth_user', JSON.stringify(user));
      if (token) localStorage.setItem('aurelius_auth_token', token);
    } catch (_) {}
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (_) {}
    try {
      localStorage.removeItem('aurelius_auth_user');
      localStorage.removeItem('aurelius_auth_token');
    } catch (_) {}
    setCurrentUser(null);
    showToast('Signed out of Aurelius Commerce Hub');
  };

  if (!currentUser) {
    return (
      <>
        <LoginView
          productCount={products.length}
          storeCount={stores.length}
          onLoginSuccess={handleLoginSuccess}
        />
        <div id="toast" className="toast" role="status" aria-live="polite"></div>
      </>
    );
  }

  return (
    <>
      <Topbar
        activeView={activeView}
        onSwitchView={(view) => switchView(view)}
        stores={stores}
        activeStoreId={activeStoreId}
        onChangeStoreId={(storeId) => {
          setActiveStoreId(storeId);
          const found = stores.find((s) => s.id === storeId);
          showToast(found ? `Switched active store to ${found.name}` : 'Showing all connected stores');
        }}
        amazonConnection={amazonConnection}
        cjConnection={cjConnection}
        currentUser={currentUser}
        orderCount={orderCount}
        onLogout={handleLogout}
        onOpenAmazonConnect={(triggerEl) => {
          lastDialogTriggerRef.current = triggerEl;
          setAmazonConnectOpen(true);
        }}
        onOpenNewStore={(triggerEl) => {
          lastDialogTriggerRef.current = triggerEl;
          setNewStoreOpen(true);
        }}
      />

      <main className="workspace-main">
        <ProductsView
          active={activeView === 'products'}
          products={products}
          stores={stores}
          activeStore={activeStore}
          activeStoreId={activeStoreId}
          selectedProduct={selectedProduct}
          activeFilter={activeFilter}
          onChangeFilter={setActiveFilter}
          onResetFilterAndStore={() => {
            setActiveFilter('all');
            setActiveStoreId('all');
          }}
          onSwitchView={switchView}
          onSelectProductAndView={(productId, view, scrollToDossier) => {
            setSelectedProductId(productId);
            switchView(view);
            if (scrollToDossier) {
              setTimeout(() => {
                const dossierEl = document.getElementById(`dossier-card-${productId}`);
                if (dossierEl) {
                  dossierEl.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
                }
              }, 60);
            }
          }}
          onOpenLightbox={(product, triggerEl) => {
            lastDialogTriggerRef.current = triggerEl;
            setLightboxProduct(product);
          }}
          onOpenIssues={(productId, triggerEl) => openIssuesDialog(productId, triggerEl)}
          onSyncSingleProductCard={handleSyncSingleProductCard}
          onQuickUpdateProduct={handleQuickUpdateProduct}
          onPullAmazon={async () => {
            await pullFromAmazon({ force: true, quiet: false });
          }}
          onSyncAllAmazon={handleSyncAllAmazon}
          onPatchAllAmazon={handlePatchAllAmazon}
          onScanKeyword={handleScanKeyword}
          syncInFlight={syncInFlight}
          pullStatusLine={pullStatusLine}
          lastPullError={lastPullError}
          sellerId={amazonConnection.sellerId}
        />

        <ResearchView
          active={activeView === 'research'}
          products={products}
          activeStore={activeStore}
          sellerId={amazonConnection.sellerId}
          onLaunchProductWithMode={(productId, mode, view) => {
            setSelectedProductId(productId);
            setSourcingMode(mode);
            switchView(view);
            showToast(
              mode === 'us_dropship'
                ? 'Loaded US Domestic Dropship Mode ($0 Upfront Inventory · 0 MOQ)!'
                : 'Loaded Alibaba Private-Label Bulk Mode!'
            );
          }}
          onOpenSyncConfirm={(product, triggerEl) => {
            lastDialogTriggerRef.current = triggerEl;
            setSyncConfirmData({ open: true, product, syncResult: null, isBulk: false, autoOrder: null });
          }}
          onTriggerAutoDropship={placeAutomatedUsDropshipOrder}
        />

        <PreviewView
          active={activeView === 'preview'}
          products={products}
          selectedProduct={selectedProduct}
          previewChannel={previewChannel}
          sellerId={amazonConnection.sellerId}
          onSelectProduct={setSelectedProductId}
          onChangePreviewChannel={setPreviewChannel}
          onSimulateOrder={placeAutomatedUsDropshipOrder}
          onSwitchView={switchView}
        />

        <LaunchView
          active={activeView === 'launch'}
          products={products}
          stores={stores}
          activeStore={activeStore}
          selectedProduct={selectedProduct}
          sourcingMode={sourcingMode}
          shipments={shipments}
          sellerId={amazonConnection.sellerId}
          marketplaceId={amazonConnection.marketplaceId || 'ATVPDKIKX0DER'}
          onSelectProduct={setSelectedProductId}
          onChangeSourcingMode={setSourcingMode}
          onToggleStoreSync={(productId, storeId) => {
            setProducts((prev) =>
              prev.map((item) => {
                if (item.id !== productId) return item;
                const currentAssigned = Array.isArray(item.assignedStores) ? item.assignedStores : stores.map((s) => s.id);
                const nextAssigned = currentAssigned.includes(storeId)
                  ? currentAssigned.filter((id) => id !== storeId)
                  : [...currentAssigned, storeId];
                const updated: Product = {
                  ...item,
                  assignedStores: nextAssigned
                };
                updated.issueSummary = computeClientIssueSummary(updated, stores);
                if (window.AureliusFirebase?.saveProduct) {
                  window.AureliusFirebase.saveProduct(updated);
                }
                showToast('Updated store sync for ' + updated.title);
                return updated;
              })
            );
          }}
          onPushSingleProductAmazon={async (prod, overrides, triggerEl) => {
            lastDialogTriggerRef.current = triggerEl;
            const { syncResult, updatedProduct } = await syncSingleProductToAmazon(prod, overrides);
            setSyncConfirmData({ open: true, product: updatedProduct, syncResult, isBulk: false, autoOrder: null });
            showToast(`Listing pushed to Amazon Seller Central! SKU: ${syncResult.sku}`);
          }}
          onQueueShipment={(record) => {
            setShipments((prev) => [record, ...prev]);
            if (window.AureliusFirebase?.saveShipment) {
              window.AureliusFirebase.saveShipment(record);
            }
            showToast(`Order ${record.poNumber} queued for ${record.warehouse}!`);
          }}
          onTriggerAutoDropship={placeAutomatedUsDropshipOrder}
          onSaveCustomUsUrl={async (productId, newUrl) => {
            try {
              await fetch('/api/dropship/update-link', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId, usDropshipUrl: newUrl })
              });
            } catch (_) {}

            setProducts((prev) =>
              prev.map((item) => {
                if (item.id !== productId) return item;
                const updated: Product = {
                  ...item,
                  researchDossier: {
                    ...(item.researchDossier || {}),
                    usDropshipUrl: newUrl
                  }
                };
                if (window.AureliusFirebase?.saveProduct) {
                  window.AureliusFirebase.saveProduct(updated);
                }
                showToast(`Saved custom US Dropship link for "${updated.title}"!`);
                return updated;
              })
            );
          }}
        />

        <StoresView
          active={activeView === 'stores'}
          stores={stores}
          products={products}
          activeStoreId={activeStoreId}
          onSelectStoreId={(storeId) => {
            setActiveStoreId(storeId);
            showToast('Active store updated!');
          }}
          onPreviewChannelAndView={(channel, view) => {
            setPreviewChannel(channel);
            switchView(view);
          }}
          onOpenAmazonConnect={(triggerEl) => {
            lastDialogTriggerRef.current = triggerEl;
            setAmazonConnectOpen(true);
          }}
          onOpenNewStore={(triggerEl) => {
            lastDialogTriggerRef.current = triggerEl;
            setNewStoreOpen(true);
          }}
        />

        <AdsView active={activeView === 'ads'} products={products} />

        <OrdersView
          active={activeView === 'orders'}
          products={products}
          sellerId={amazonConnection.sellerId}
          onSelectProductAndView={(productId, view) => {
            setSelectedProductId(productId);
            switchView(view);
          }}
          onOrdersCountChange={setOrderCount}
        />

        <CalendarView active={activeView === 'calendar'} products={products} />

        <ReportsView active={activeView === 'reports'} products={products} />
      </main>

      <Modals
        amazonConnectOpen={amazonConnectOpen}
        onCloseAmazonConnect={() => {
          setAmazonConnectOpen(false);
          restoreTriggerFocus();
        }}
        amazonConnection={amazonConnection}
        cjConnection={cjConnection}
        onSaveCjKey={handleSaveCjKey}
        onSaveAmazonConnection={async ({ sellerId, marketplaceId, lwaClientId, lwaClientSecret, lwaRefreshToken }) => {
          let connectedData: AmazonConnection | null = null;
          try {
            const res = await fetch('/api/amazon/connect', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sellerId,
                marketplaceId,
                lwaClientId,
                lwaClientSecret,
                lwaRefreshToken
              })
            });
            if (res.ok) {
              const data = await res.json();
              connectedData = data.connection;
              if (Array.isArray(data.stores)) setStores(data.stores);
            }
          } catch (_) {}

          const hasFullOAuth = Boolean(lwaClientId && lwaClientSecret && lwaRefreshToken);
          const nextConn: AmazonConnection = connectedData || {
            connected: true,
            sellerId,
            marketplaceId,
            lwaClientId,
            lwaClientSecret,
            lwaRefreshToken,
            hasLwaCredentials: hasFullOAuth,
            mode: hasFullOAuth ? 'LIVE_SP_API' : 'INSTANT_SANDBOX',
            connectedAt: new Date().toISOString()
          };

          setAmazonConnection(nextConn);
          try {
            localStorage.setItem('aurelius_amz_connection', JSON.stringify(nextConn));
          } catch (_) {}

          setStores((prev) =>
            prev.map((s) => {
              if (s.id === 'store-amz-us' || s.channelKey === 'amazon') {
                const updatedStore = { ...s, status: `Connected · SP-API (${sellerId})` };
                if (window.AureliusFirebase?.saveStore) {
                  window.AureliusFirebase.saveStore(updatedStore);
                }
                return updatedStore;
              }
              return s;
            })
          );

          setAmazonConnectOpen(false);
          restoreTriggerFocus();
          showToast(`Connected Amazon Seller Central (Merchant ID: ${sellerId})!`);
        }}
        newStoreOpen={newStoreOpen}
        onCloseNewStore={() => {
          setNewStoreOpen(false);
          restoreTriggerFocus();
        }}
        onCreateNewStore={({ name, channelKey, nicheFocus }) => {
          const channelPresets: Record<
            string,
            { channel: string; referralRate: number; referralRateLabel: string; avgFulfillmentFee: number; fulfillment: string }
          > = {
            amazon: { channel: 'Amazon US (FBA Prime)', referralRate: 0.15, referralRateLabel: '15%', avgFulfillmentFee: 4.75, fulfillment: 'Amazon FBA (ONT8)' },
            shopify: { channel: 'Shopify Direct-to-Consumer', referralRate: 0.029, referralRateLabel: '2.9%', avgFulfillmentFee: 5.4, fulfillment: 'Amazon MCF / 3PL' },
            walmart: { channel: 'Walmart Marketplace (WFS)', referralRate: 0.15, referralRateLabel: '15%', avgFulfillmentFee: 4.25, fulfillment: 'Walmart WFS' },
            tiktok: { channel: 'TikTok Shop US', referralRate: 0.06, referralRateLabel: '6.0%', avgFulfillmentFee: 3.9, fulfillment: 'Fulfilled by TikTok' },
            ebay: { channel: 'eBay Brand Outlet', referralRate: 0.1325, referralRateLabel: '13.25%', avgFulfillmentFee: 5.1, fulfillment: '3PL Auto-Fulfill' }
          };
          const preset = channelPresets[channelKey] || channelPresets.amazon;
          const newStore: Store = {
            id: `store-${channelKey}-${Date.now().toString().slice(-4)}`,
            name,
            channelKey,
            ...preset,
            status: 'Connected · Active',
            monthlyRevenueEst: 24500,
            monthlyRevenueFormatted: '$24,500',
            storefrontUrl: `${name.toLowerCase().replace(/[^a-z0-9]/g, '')}.shop`,
            nicheFocus: nicheFocus || 'High-Margin Arbitrage'
          };

          setStores((prev) => [...prev, newStore]);
          setProducts((prev) =>
            prev.map((p) => ({
              ...p,
              assignedStores: Array.isArray(p.assignedStores) ? [...p.assignedStores, newStore.id] : [newStore.id]
            }))
          );

          if (window.AureliusFirebase?.saveStore) {
            window.AureliusFirebase.saveStore(newStore);
          }

          setNewStoreOpen(false);
          restoreTriggerFocus();
          switchView('stores');
          showToast(`Created storefront "${newStore.name}"!`);
        }}
        lightboxProduct={lightboxProduct}
        onCloseLightbox={() => {
          setLightboxProduct(null);
          restoreTriggerFocus();
        }}
        syncConfirmData={syncConfirmData}
        onCloseSyncConfirm={() => {
          setSyncConfirmData((prev) => ({ ...prev, open: false }));
          restoreTriggerFocus();
        }}
        products={products}
        stores={stores}
        issuesDialogState={issuesDialogState}
        onCloseIssuesDialog={() => {
          setIssuesDialogState((prev) => ({ ...prev, open: false }));
          restoreTriggerFocus();
        }}
        onRecheckIssuesLive={(productId) => {
          loadIssuesIntoDialog(productId, { live: true });
        }}
      />

      <div id="toast" className="toast" role="status" aria-live="polite"></div>
    </>
  );
};
